import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Users, Eye, EyeOff, Loader2, CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    department: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.first_name || !formData.last_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...userData } = formData;
      const response = await axios.post(`${API_URL}/api/auth/register`, userData);
      
      setRegistrationStatus(response.data.status);
      setRegistrationComplete(true);
      
      if (response.data.status === 'active') {
        // First user - auto activated, redirect to login
        toast.success('Compte créé et activé ! Vous pouvez vous connecter.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        // Pending - show waiting message
        toast.success('Demande envoyée !');
      }
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  // Show success/pending screen after registration
  if (registrationComplete) {
    return (
      <div className="auth-container" data-testid="register-page">
        <div className="auth-form-section bg-slate-50">
          <div className="w-full max-w-md mx-auto">
            {/* Logo */}
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Espace Employés</h1>
                <p className="text-sm text-slate-500">Portail de gestion</p>
              </div>
            </div>

            <Card className="border-0 shadow-lg">
              <CardContent className="pt-8 pb-8">
                {registrationStatus === 'active' ? (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">Compte activé !</h2>
                    <p className="text-slate-600 mb-6">
                      Votre compte administrateur a été créé et activé automatiquement.
                    </p>
                    <Button 
                      onClick={() => navigate('/login')}
                      className="bg-slate-900 hover:bg-slate-800"
                    >
                      Se connecter
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="w-8 h-8 text-amber-600" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">Demande envoyée !</h2>
                    <p className="text-slate-600 mb-4">
                      Votre demande d'inscription a bien été reçue.
                    </p>
                    <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                      <h3 className="font-medium text-slate-900 mb-2">Prochaines étapes :</h3>
                      <ol className="text-sm text-slate-600 space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="bg-slate-200 text-slate-700 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs">1</span>
                          <span>Un administrateur va examiner votre demande</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-slate-200 text-slate-700 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs">2</span>
                          <span>Une fois validée, vous recevrez un email de confirmation</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-slate-200 text-slate-700 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs">3</span>
                          <span>Vous pourrez alors vous connecter avec vos identifiants</span>
                        </li>
                      </ol>
                    </div>
                    <p className="text-sm text-slate-500">
                      L'email sera envoyé à : <strong>{formData.email}</strong>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="mt-6 text-center text-sm">
              <Link 
                to="/login" 
                className="text-blue-600 hover:underline font-medium"
              >
                Retour à la connexion
              </Link>
            </div>
          </div>
        </div>

        {/* Image section */}
        <div className="auth-image-section">
          <img
            src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmclMjBkaXZlcnNlfGVufDB8fHx8MTc3MDA0NTUzOHww&ixlib=rb-4.1.0&q=85"
            alt="Business meeting"
            className="w-full h-full object-cover"
          />
          <div className="auth-image-overlay flex flex-col justify-end p-12">
            <blockquote className="text-white space-y-4">
              <p className="text-2xl font-medium leading-relaxed">
                "Merci pour votre inscription ! Nous avons hâte de vous accueillir."
              </p>
              <footer className="text-slate-300">
                — Équipe Espace Employés
              </footer>
            </blockquote>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container" data-testid="register-page">
      {/* Form section */}
      <div className="auth-form-section bg-slate-50">
        <div className="w-full max-w-md mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Espace Employés</h1>
              <p className="text-sm text-slate-500">Portail de gestion</p>
            </div>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl">Créer un compte</CardTitle>
              <CardDescription>
                Remplissez le formulaire pour demander un accès
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Prénom *</Label>
                    <Input
                      id="first_name"
                      placeholder="Jean"
                      value={formData.first_name}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      disabled={loading}
                      data-testid="register-firstname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Nom *</Label>
                    <Input
                      id="last_name"
                      placeholder="Dupont"
                      value={formData.last_name}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      disabled={loading}
                      data-testid="register-lastname-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Adresse email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="nom@entreprise.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    disabled={loading}
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Département</Label>
                  <Input
                    id="department"
                    placeholder="RH, IT, Marketing, etc."
                    value={formData.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                    disabled={loading}
                    data-testid="register-department-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      disabled={loading}
                      data-testid="register-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-slate-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-slate-400" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    disabled={loading}
                    data-testid="register-confirm-password-input"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700">
                  <p className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Votre demande sera validée par un administrateur
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer ma demande'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm">
                <span className="text-slate-500">Déjà un compte ? </span>
                <Link 
                  to="/login" 
                  className="text-blue-600 hover:underline font-medium"
                  data-testid="login-link"
                >
                  Se connecter
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image section */}
      <div className="auth-image-section">
        <img
          src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmclMjBkaXZlcnNlfGVufDB8fHx8MTc3MDA0NTUzOHww&ixlib=rb-4.1.0&q=85"
          alt="Business meeting"
          className="w-full h-full object-cover"
        />
        <div className="auth-image-overlay flex flex-col justify-end p-12">
          <blockquote className="text-white space-y-4">
            <p className="text-2xl font-medium leading-relaxed">
              "Rejoignez notre plateforme et simplifiez la gestion de vos équipes."
            </p>
            <footer className="text-slate-300">
              — Équipe Espace Employés
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
